// src/index.js
import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";

// ===== RUNTIME STABILIZER (install BEFORE React renders) =====
(function installGlobalGuards(){
  if (typeof window === "undefined" || window.__canmoreGuardsInstalled) return;
  window.__canmoreGuardsInstalled = true;

  const describe = (reason) => {
    try {
      if (reason instanceof Error) return `${reason.name}: ${reason.message}\n${reason.stack || ""}`;
      if (reason && typeof reason.clone === "function") return `Response(${reason.status || "?"}) ${reason.url || ""}`;
      if (typeof reason === "object") return JSON.stringify(reason);
      return String(reason);
    } catch { return String(reason); }
  };

  const onRejection = (event) => {
    try { event.preventDefault(); } catch {}
    const r = event.reason;
    if (r && typeof r.clone === "function") {
      r.clone().text()
        .then((t) => console.error("[UnhandledRejection][HTTP]", r.status, r.url || "", t.slice(0, 400)))
        .catch(() => console.error("[UnhandledRejection][HTTP]", r.status, r.url || "", "(body read failed)"));
    } else {
      console.error("[UnhandledRejection]", describe(r), r);
    }
  };

  const onError = (event) => {
    try { event.preventDefault(); } catch {}
    console.error("[WindowError]", event.message, event.error || "");
  };

  // Capture phase so CRA overlay can't steal it first
  window.addEventListener("unhandledrejection", onRejection, { capture: true });
  window.addEventListener("error", onError, { capture: true });

  // Wrap fetch to log non-2xx and rejections (helps identify the real culprit)
  if (window.fetch && !window.__fetchWrapped) {
    const _fetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
      try {
        const res = await _fetch(...args);
        if (!res.ok) {
          try {
            const body = await res.clone().text();
            console.error("[FetchNotOK]", args[0], res.status, body.slice(0, 400));
          } catch {
            console.error("[FetchNotOK]", args[0], res.status, "(body read failed)");
          }
        }
        return res;
      } catch (e) {
        console.error("[FetchReject]", args[0], e);
        throw e;
      }
    };
    window.__fetchWrapped = true;
  }

  // Defang CRA overlay (safe no-op if not present)
  const hook = window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__ || window.__REACT_DEV_OVERLAY_GLOBAL_HOOK__;
  try {
    if (hook?.onUnhandledError) hook.onUnhandledError = (e) => console.error("[OverlayError]", e);
    if (hook?.onUnhandledRejection) hook.onUnhandledRejection = (e) => console.error("[OverlayRejection]", e);
  } catch {}
})();

// ===== Render App =====
const container = document.getElementById("root");
const root = createRoot(container);
root.render(<App />);